// Sky dome, sun / moon, day-night cycle, fog, shadows, roaming lamp lights.
import * as THREE from 'three';
import { G } from './ctx';
import type { IEnvironment } from './types';
import type { CityExtras } from './city';
import { makeRng } from './util';

const SUNRISE = 5.5;
const SUNSET = 18.5;
const MAX_EL = (62 * Math.PI) / 180;

const smooth = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export class Environment implements IEnvironment {
  dayTime = 17.5;
  nightFactor = 0;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  fog: THREE.Fog;
  /** unit vector toward the sun (or moon at night) */
  lightDir = new THREE.Vector3(0, 1, 0);
  sunDir = new THREE.Vector3(0, 1, 0);
  /** sky palette outputs (linear) */
  horizon = new THREE.Color();
  zenith = new THREE.Color();
  sunColor = new THREE.Color();

  private scene: THREE.Scene;
  private skyGroup = new THREE.Group();
  private skyMat: THREE.ShaderMaterial;
  private starMat: THREE.PointsMaterial;
  private lamps: THREE.PointLight[] = [];
  private lampTimer = 0;
  private tmp = new THREE.Vector3();
  private right = new THREE.Vector3();
  private up = new THREE.Vector3();
  private snapped = new THREE.Vector3();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    // ---- lights ------------------------------------------------------------------
    this.sun = new THREE.DirectionalLight(0xffffff, 2.5);
    this.sun.position.set(100, 150, 50);
    this.sun.castShadow = true;
    const sc = this.sun.shadow.camera;
    sc.left = -75;
    sc.right = 75;
    sc.top = 75;
    sc.bottom = -75;
    sc.near = 5;
    sc.far = 520;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.00035;
    this.sun.shadow.normalBias = 0.05;
    this.sun.shadow.radius = 2;
    scene.add(this.sun);
    scene.add(this.sun.target);
    this.hemi = new THREE.HemisphereLight(0x88aaff, 0x55503a, 0.9);
    scene.add(this.hemi);
    this.fog = new THREE.Fog(0x87b6e0, 180, 650);
    scene.fog = this.fog;
    scene.background = new THREE.Color(0x87b6e0);

    // ---- sky dome ------------------------------------------------------------------
    this.skyMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      uniforms: {
        uZenith: { value: new THREE.Color(0.2, 0.45, 0.95) },
        uHorizon: { value: new THREE.Color(0.75, 0.85, 0.95) },
        uGround: { value: new THREE.Color(0.35, 0.4, 0.45) },
        uSunDir: { value: new THREE.Vector3(0, 1, 0) },
        uSunColor: { value: new THREE.Color(1, 0.9, 0.7) },
        uSunSize: { value: Math.cos(0.012) },
        uSunGlow: { value: 1 },
        uMoonDir: { value: new THREE.Vector3(0, -1, 0) },
        uMoonA: { value: 0 },
        uHaze: { value: 2.2 },
      },
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = position;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uZenith; uniform vec3 uHorizon; uniform vec3 uGround;
        uniform vec3 uSunDir; uniform vec3 uSunColor; uniform float uSunSize; uniform float uSunGlow;
        uniform vec3 uMoonDir; uniform float uMoonA; uniform float uHaze;
        varying vec3 vDir;
        void main() {
          vec3 d = normalize(vDir);
          float y = d.y;
          float t = pow(clamp(1.0 - max(y, 0.0), 0.0, 1.0), uHaze);
          vec3 col = mix(uZenith, uHorizon, t);
          if (y < 0.0) col = mix(uHorizon, uGround, clamp(-y * 6.0, 0.0, 1.0));
          float sd = dot(d, uSunDir);
          float disc = smoothstep(uSunSize - 0.0015, uSunSize + 0.0008, sd);
          float glow = pow(max(sd, 0.0), 18.0) * 0.28 + pow(max(sd, 0.0), 120.0) * 0.55;
          col += uSunColor * (disc * 5.0 + glow * uSunGlow);
          float md = dot(d, uMoonDir);
          float mdisc = smoothstep(0.99955, 0.99975, md);
          col += vec3(0.85, 0.9, 1.0) * (mdisc * 1.6 + pow(max(md, 0.0), 200.0) * 0.12) * uMoonA;
          gl_FragColor = vec4(col, 1.0);
        }`,
    });
    const dome = new THREE.Mesh(new THREE.SphereGeometry(1350, 40, 24), this.skyMat);
    dome.frustumCulled = false;
    dome.renderOrder = -10;
    dome.name = 'sky';
    this.skyGroup.add(dome);
    // stars
    const rng = makeRng(4242);
    const n = 1800;
    const pos = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const u = rng() * 2 - 1;
      const th = rng() * Math.PI * 2;
      const y = 0.03 + Math.abs(u) * 0.97;
      const r = Math.sqrt(1 - y * y);
      pos[i * 3] = Math.cos(th) * r * 1300;
      pos[i * 3 + 1] = y * 1300;
      pos[i * 3 + 2] = Math.sin(th) * r * 1300;
    }
    const sg = new THREE.BufferGeometry();
    sg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.starMat = new THREE.PointsMaterial({ color: 0xdfe8ff, size: 2.2, sizeAttenuation: false, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
    const stars = new THREE.Points(sg, this.starMat);
    stars.frustumCulled = false;
    stars.renderOrder = -9;
    stars.name = 'stars';
    this.skyGroup.add(stars);
    this.skyGroup.name = 'skyGroup';
    scene.add(this.skyGroup);

    // ---- roaming lamp lights ------------------------------------------------------
    for (let i = 0; i < 6; i++) {
      const pl = new THREE.PointLight(0xffc27a, 0, 26, 2);
      pl.position.set(0, 7.2, 0);
      pl.name = 'lampLight' + i;
      scene.add(pl);
      this.lamps.push(pl);
    }
    this.update(0, new THREE.Vector3());
  }

  update(dt: number, focus: THREE.Vector3): void {
    if (dt > 0) {
      this.dayTime += dt / 60;
      if (this.dayTime >= 24) this.dayTime -= 24;
    }
    const t = this.dayTime;
    // ---- sun geometry ------------------------------------------------------------
    const dayLen = SUNSET - SUNRISE;
    const phase = ((t - SUNRISE) / dayLen) % 1;
    const p = phase < 0 ? phase + 1 : phase;
    const el = Math.sin(p * Math.PI) * MAX_EL; // positive by day, negative at night
    const az = p * Math.PI;
    const ce = Math.cos(el);
    this.sunDir.set(Math.cos(az) * ce, Math.sin(el), 0.45 * ce).normalize();
    const s = this.sunDir.y;
    const moonEl = Math.max(0.25, -el * 0.75);
    const moonDir = this.tmp.set(-Math.cos(az) * Math.cos(moonEl), Math.sin(moonEl), -0.5 * Math.cos(moonEl)).normalize();

    // ---- palette -------------------------------------------------------------------
    const wN = 1 - smooth(-0.2, 0.03, s);
    const wD = smooth(0.12, 0.5, s);
    const wG = Math.max(0, 1 - wN - wD);
    const mix3 = (out: THREE.Color, n: [number, number, number], g: [number, number, number], d: [number, number, number]) =>
      out.setRGB(n[0] * wN + g[0] * wG + d[0] * wD, n[1] * wN + g[1] * wG + d[1] * wD, n[2] * wN + g[2] * wG + d[2] * wD);
    mix3(this.zenith, [0.012, 0.018, 0.05], [0.22, 0.32, 0.62], [0.2, 0.46, 0.98]);
    mix3(this.horizon, [0.05, 0.06, 0.11], [1.05, 0.55, 0.3], [0.78, 0.86, 0.95]);
    mix3(this.sunColor, [0.55, 0.65, 0.95], [1.0, 0.58, 0.3], [1.0, 0.96, 0.9]);
    this.nightFactor = 1 - smooth(-0.16, 0.06, s);

    const sm = this.skyMat.uniforms;
    (sm.uZenith.value as THREE.Color).copy(this.zenith);
    (sm.uHorizon.value as THREE.Color).copy(this.horizon);
    (sm.uGround.value as THREE.Color).copy(this.horizon).multiplyScalar(0.55);
    (sm.uSunDir.value as THREE.Vector3).copy(this.sunDir);
    (sm.uSunColor.value as THREE.Color).copy(this.sunColor).multiplyScalar(1 - wN * 0.98);
    sm.uSunGlow.value = 1 + wG * 1.6;
    sm.uHaze.value = 2.4 - wG * 0.8;
    (sm.uMoonDir.value as THREE.Vector3).copy(moonDir);
    sm.uMoonA.value = wN;
    this.starMat.opacity = wN * 0.95;
    this.skyGroup.position.copy(G.camera.position);

    // ---- lights --------------------------------------------------------------------
    const sunI = 2.7 * smooth(-0.03, 0.22, s);
    const moonI = 0.22 * wN;
    const moonMode = wN > 0.5 && sunI < 0.05;
    if (moonMode) {
      this.lightDir.copy(moonDir);
      this.sun.color.setRGB(0.55, 0.65, 0.95);
      this.sun.intensity = moonI;
    } else {
      this.lightDir.copy(this.sunDir);
      this.sun.color.copy(this.sunColor);
      this.sun.intensity = sunI + moonI;
    }
    this.hemi.color.copy(this.zenith).lerp(this.horizon, 0.35);
    this.hemi.groundColor.setRGB(0.32 - wN * 0.28, 0.29 - wN * 0.26, 0.24 - wN * 0.2);
    this.hemi.intensity = 1.0 - wN * 0.62;

    // fog tracks the horizon, closer at night
    this.fog.color.copy(this.horizon);
    (this.scene.background as THREE.Color).copy(this.horizon);
    this.fog.near = 180 - wN * 110;
    this.fog.far = 650 - wN * 320;

    // ---- shadow frustum follows the focus (snapped to texels) ---------------------
    const size = 150;
    const mapSize = this.sun.shadow.mapSize.x || 2048;
    const texel = size / mapSize;
    this.right.set(0, 1, 0).cross(this.lightDir);
    if (this.right.lengthSq() < 1e-6) this.right.set(1, 0, 0);
    this.right.normalize();
    this.up.copy(this.lightDir).cross(this.right).normalize();
    const fx = focus.dot(this.right), fy = focus.dot(this.up);
    const sx = Math.round(fx / texel) * texel - fx, sy = Math.round(fy / texel) * texel - fy;
    this.snapped.copy(focus).addScaledVector(this.right, sx).addScaledVector(this.up, sy);
    this.sun.position.copy(this.snapped).addScaledVector(this.lightDir, 240);
    this.sun.target.position.copy(this.snapped);
    this.sun.target.updateMatrixWorld();

    // ---- roaming point lights at the nearest lamps ----------------------------------
    this.lampTimer -= dt;
    const nf = this.nightFactor;
    if (this.lampTimer <= 0) {
      this.lampTimer = 0.3;
      const city = G.city as unknown as Partial<CityExtras> | undefined;
      const lp = city?.lampPositions;
      if (lp && nf > 0.02) {
        const best: { d: number; i: number }[] = [];
        for (let i = 0; i < lp.length / 2; i++) {
          const dx = lp[i * 2] - focus.x, dz = lp[i * 2 + 1] - focus.z;
          const d = dx * dx + dz * dz;
          if (d > 60 * 60) continue;
          if (best.length < 6) {
            best.push({ d, i });
            best.sort((a, b) => a.d - b.d);
          } else if (d < best[5].d) {
            best[5] = { d, i };
            best.sort((a, b) => a.d - b.d);
          }
        }
        this.lamps.forEach((pl, k) => {
          const b = best[k];
          if (b) {
            pl.position.set(lp[b.i * 2], 7.0, lp[b.i * 2 + 1]);
            pl.intensity = 55 * nf;
          } else pl.intensity = 0;
        });
      } else for (const pl of this.lamps) pl.intensity = 0;
    }
    // water uniforms (if the city exposes them)
    const wu = (G.city as unknown as Partial<CityExtras> | undefined)?.waterUniforms;
    if (wu) {
      (wu.uSky.value as THREE.Color).copy(this.zenith).lerp(this.horizon, 0.5);
      (wu.uSunDir.value as THREE.Vector3).copy(this.lightDir);
      (wu.uSunColor.value as THREE.Color).copy(this.sun.color).multiplyScalar(Math.min(1, this.sun.intensity / 2.7));
    }
  }
}
